import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ImageOff, Settings } from "lucide-react";
import type { HsnCode } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { EmptyState, LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { renderCell } from "@/components/table/cells";
import { Button } from "@/components/ui/button";
import { CATALOG_FIELDS } from "@/modules/catalog/fields";

const STATUS_FIELD = CATALOG_FIELDS.find((field) => field.name === "status");

/**
 * One HSN code and the products carrying it.
 *
 * Rates are shown, not edited: the user asked for them to be changed in GST
 * Settings (2026-09-14), which is where someone checking a tax rate would look,
 * and one place to edit a rate is one place to audit it.
 *
 * A product opens in Products rather than stacking a second drawer here: the
 * product drawer carries its own gallery and dialog, and two drawers deep is
 * where Escape stops being predictable.
 */
export function HsnCodeDetail({ hsn }: { hsn: HsnCode }) {
  const data = useDataSource();
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ["catalog", "hsn", "products", hsn.code],
    queryFn: () => data.catalog.listProductsByHsn(hsn.code, { limit: 250 }),
  });

  return (
    <div className="space-y-4 p-4">
      <header className="space-y-2">
        <div className="flex flex-wrap items-baseline gap-x-3">
          <h2 className="text-lg font-semibold tabular-nums">{hsn.code}</h2>
          <p className="text-muted-foreground text-sm">{hsn.description}</p>
        </div>
        <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <div className="flex gap-2">
            <dt className="text-muted-foreground">GST rate</dt>
            <dd className="text-primary-strong font-semibold tabular-nums">
              {hsn.gstRate}%
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-muted-foreground">Cess</dt>
            <dd className="font-medium tabular-nums">{hsn.cessRate}%</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-muted-foreground">Products</dt>
            <dd className="font-medium tabular-nums">
              {hsn.productCount ?? "—"}
            </dd>
          </div>
        </dl>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            void navigate({ to: "/gst/settings" });
          }}
        >
          <Settings />
          Edit rates in GST Settings
        </Button>
      </header>

      {query.isPending ? (
        <TableSkeleton rows={6} />
      ) : query.isError ? (
        <LoadFailed
          message={query.error.message}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : query.data.items.length === 0 ? (
        <EmptyState
          title="No products use this code"
          description="A code stays in the master after the last product carrying it moves to another."
        />
      ) : (
        <ul className="divide-y rounded-lg border">
          {query.data.items.map((product) => (
            <li key={product.id}>
              <button
                type="button"
                onClick={() => {
                  void navigate({
                    to: "/catalog",
                    search: { record: product.id },
                  });
                }}
                className="hover:bg-muted/50 focus-visible:ring-ring flex w-full items-center gap-3 px-3 py-2 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
              >
                {product.featuredImageUrl ? (
                  <img
                    src={product.featuredImageUrl}
                    alt=""
                    loading="lazy"
                    className="size-10 shrink-0 rounded object-cover"
                  />
                ) : (
                  <div
                    className="bg-muted text-muted-foreground flex size-10 shrink-0 items-center justify-center rounded"
                    aria-hidden
                  >
                    <ImageOff className="size-4" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {product.title}
                  </p>
                  <p className="text-muted-foreground truncate text-xs">
                    {[product.vendor, product.productType]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </p>
                </div>
                {STATUS_FIELD ? renderCell(STATUS_FIELD, product.status) : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
